import '@angular/compiler';
import {
  Component,
  ComponentRef,
  Inject,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { RulesActions, RulesConditions, RulesTag, Step } from '@app/features/automation-rules/utils';
import { SettingsService } from '@app/services/settings/settings.service';
import { Store } from '@ngrx/store';
import { OrderStatus, ReturnPreferences } from '@pdqdev/pdq-shared/dist/entities';
import { CountryCode } from '@pdqdev/pdq-shared/dist/utils/Country-utils';
import { Subscription } from 'rxjs';
import { AutomationRulesActions } from '../../../actions';
import { AutomationRulesReducer } from '../../../reducers';
import { RuleTemplateSelectorComponent } from '../../rule-template/rule-template-selector.component';

interface DialogData {
  step: Step;
}

@Component({
  selector: 'automation-rule-create-dialog',
  templateUrl: './automation-rule-create-dialog.html',
  styleUrls: ['./automation-rule-create-dialog.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class AutomationRuleCreateDialog implements OnInit, OnDestroy {
  componentRef: ComponentRef<any>;

  step: Step;
  selectedConditions: AutomationRulesActions.SelectedCondition[][];
  selectedAction: AutomationRulesActions.SelectedAction;
  internalName: UntypedFormControl;

  templateData$ = this.store.select(AutomationRulesReducer.selectTemplateData);
  templateDataSubscription$: Subscription;
  notVerifiedName;
  @ViewChild('target', { read: ViewContainerRef, static: true })
  vcRef: ViewContainerRef;
  displayedMenuItems = [
    {
      text: Step.verify,
    },
    {
      text: Step.assign,
    },
    {
      text: Step.fulfill,
    },
    {
      text: Step.track,
    },
  ];
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    public dialogRef: MatDialogRef<AutomationRuleCreateDialog>,
    private store: Store,
    private settingsService: SettingsService,
  ) {}

  ngOnInit() {
    this.initDialogData();
    this.createComponent();

    this.componentRef.instance.onChangeConditionSelect.subscribe(({ value, index }) => {
      const row = index[0];
      const column = index[1];
      if (!this.selectedConditions) {
        this.selectedConditions = [[]];
        this.selectedConditions[0] = [value];
      } else if (this.selectedConditions.length > row) {
        if (this.selectedConditions[row].length > column) {
          this.selectedConditions[row][column] = value;
        } else {
          this.selectedConditions[row].push(value);
        }
      } else {
        this.selectedConditions[row] = [value];
      }
    });
    this.componentRef.instance.onChangeActionSelect.subscribe((value) => (this.selectedAction = value));
    this.componentRef.instance.onChangeNotVerifiedName?.subscribe((value) => {
      this.notVerifiedName = value;
    });
    this.templateDataSubscription$ = this.templateData$.subscribe(
      (templateData?: AutomationRulesReducer.TemplateData) => {
        if (templateData) {
          this.showTemplateComponent({
            parameters: templateData.parameters,
            template: templateData['ng-template'],
            notVerifiedName: templateData.notVerifiedName,
          });
        }
      },
    );
    this.componentRef.instance.onChangeOperatorSelect.subscribe(({ operator, is, index }) => {
      const row = index[0];
      const column = index[1];

      if (this.selectedConditions[row][column].value) {
        if (typeof is !== 'undefined') {
          this.selectedConditions[row][column].value.is = is;
        }
        this.selectedConditions[row][column].value.operator = operator;
      }
    });

    this.componentRef.instance.onChangeOperator2Select.subscribe(({ operator, is, index }) => {
      const row = index[0];
      const column = index[1];
      const value = { ...this.selectedConditions[row][column].value };
      if (typeof is !== 'undefined') {
        value.is = is;
      }
      value.operator2 = operator;
      this.selectedConditions[row][column].value = value;
    });

    this.componentRef.instance.onRemoveCondition.subscribe(({ index }) => {
      const row = index[0];
      const column = index[1];
      if (this.selectedConditions[row].length === 1) {
        this.selectedConditions.splice(row, 1);
      } else {
        this.selectedConditions[row].splice(column, 1);
      }
    });
  }

  ngOnDestroy() {
    if (this.componentRef) {
      this.componentRef.destroy();
    }
    this.templateDataSubscription$?.unsubscribe();
  }

  initDialogData() {
    if (!this.data) {
      return;
    }
    this.internalName = new UntypedFormControl();
    this.step = this.data.step;
  }

  createComponent() {
    if (!this.componentRef) {
      this.componentRef = this.vcRef.createComponent(RuleTemplateSelectorComponent);
    }
  }

  showTemplateComponent(data: {
    template: string;
    parameters: {
      conditions: Array<any>;
      actions: Array<any>;
    };
    notVerifiedName: string;
  }) {
    this.createComponent();

    this.componentRef.instance.template = data.template;
    this.componentRef.instance.conditions = data.parameters.conditions;
    this.componentRef.instance.actions = data.parameters.actions;
    this.componentRef.instance.notVerifiedName = data.notVerifiedName;
  }

  addWorkflow(): void {
    this.componentRef.instance.template = null;
    if (this.notVerifiedName && this.selectedAction.value === 'notVerified') {
      this.settingsService
        .saveVerifications([{ path: 'notVerifiedName', value: this.notVerifiedName }])
        .subscribe(() => {
          this.addRule();
        });
    } else {
      this.addRule();
    }
  }

  addRule(): void {
    this.store.dispatch(
      AutomationRulesActions.addRule({
        selectedConditions: this.selectedConditions,
        selectedAction: this.selectedAction,
        internalName: this.internalName.value,
      }),
    );
  }

  onBackHandler(): void {
    this.onClose();
    this.store.dispatch(
      AutomationRulesActions.goBackToAutomationRulesListDialog({
        step: this.step,
      }),
    );
  }

  isConditionsInsertedProperly(selectedConditions: AutomationRulesActions.SelectedCondition[][]): boolean {
    return selectedConditions?.every((conditionGroup: AutomationRulesActions.SelectedCondition[]) => {
      return conditionGroup.every((selectedCondition: AutomationRulesActions.SelectedCondition) => {
        switch (selectedCondition.name) {
          case RulesConditions.ShippingCountry:
            return (
              selectedCondition.value?.countries?.length &&
              selectedCondition.value?.countries.every((country) => <any>Object.values(CountryCode).includes(country))
            );
          case RulesConditions.ShippingState:
            return selectedCondition.value?.states?.length;
          case RulesConditions.OrderTag:
            return ['includes', 'excludes'].includes(selectedCondition.value?.operator?.value)
              ? selectedCondition.value?.selectedTags?.length
              : selectedCondition.value?.value;
          case RulesConditions.TotalOrderWeight:
          case RulesConditions.ItemQuantity:
          case RulesConditions.TotalOrderValue:
            return !isNaN(selectedCondition.value?.amount) && selectedCondition.value?.amount >= 0;
          case RulesConditions.InventoryLocation:
            return selectedCondition.value?.address;
          case RulesConditions.BoxChosen:
            return selectedCondition.value?.boxesArray?.length;
          case RulesConditions.NotFilteredBy:
            return selectedCondition.value?.filtersArray?.length;
          case RulesConditions.OrderSKU:
            return ['includes', 'excludes'].includes(selectedCondition.value?.operator?.value)
              ? selectedCondition.value?.selectedSkus?.length
              : selectedCondition.value?.value;
          case RulesConditions.OrderStatusFix:
            return selectedCondition.value?.statusArray?.length;
          case RulesConditions.DeliveryMethod:
            return selectedCondition.value?.operator && selectedCondition.value?.values?.length;
          case RulesConditions.PaymentStatus:
            return selectedCondition.value?.statuses?.length;
          case RulesConditions.DeliveryStatus:
            return selectedCondition.value?.deliveryStatus?.length;
        }
        return selectedCondition.value;
      });
    });
  }

  isActionInsertedProperly(selectedAction: AutomationRulesActions.SelectedAction): boolean {
    if (!selectedAction) {
      return false;
    }
    switch (selectedAction.name) {
      case 'changeOrderStatusWithDelay':
        return selectedAction.value?.value && selectedAction.value?.delayTime > 1; // Cron minimum is 1 minute
      case 'setRecommendedCarrier':
        return (
          selectedAction.value?.shipmentPreferncesFields?.carrier?.length ||
          selectedAction.value?.shipmentPreferncesFields?.maximalTransitTime != null ||
          (selectedAction.value?.shipOn?.shipOn &&
            typeof selectedAction.value?.shipOn?.x === 'number' &&
            (!selectedAction.value?.shipOn?.shipOn?.value?.includes('Before') || selectedAction.value?.shipOn?.before))
        );
      case 'setReturnPreferences':
        const value: ReturnPreferences = selectedAction.value ?? {};
        return !!(value.active && value.box?.id && value.returnToAddress?.id);
      case 'snoozeOrder':
        return (
          selectedAction.value?.snoozeBy &&
          selectedAction.value?.x != null &&
          (!selectedAction.value?.snoozeBy?.value?.includes('Before') || selectedAction.value?.before)
        );
      case 'setShipmentExtras':
        return (
          selectedAction.value && !!Object.values(selectedAction.value?.shipmentExtrasFields)?.find((v) => v == true)
        );
      case 'addTeamNote':
        return selectedAction?.value?.message?.trim()?.length;
      case 'changeDeliveryMethod':
        return selectedAction?.value?.trim()?.length;
      case 'setColorCoding':
        return !!(selectedAction?.value as string[]);
      case RulesActions.SetWeightToSKU:
        return selectedAction?.value?.weight != null && selectedAction?.value?.sku?.trim()?.length > 0;
      case RulesActions.SplitOrderByStrategy:
        return this.checkSplitByActionValidity(selectedAction?.value);
      case RulesActions.AddTags:
        return selectedAction.value?.length;
      case RulesActions.PinOrder:
        return true;
      case RulesActions.MarkAsVerified:
        return true;
      case RulesActions.FulfillOrder:
        return true;
    }
    return selectedAction.value;
  }

  isConditionsAndActionsWorkTogether(
    selectedConditions: AutomationRulesActions.SelectedCondition[][],
    selectedAction: AutomationRulesActions.SelectedAction,
  ) {
    if (!selectedAction || !selectedConditions?.length) {
      return false;
    }
    switch (selectedAction.name) {
      case RulesActions.SetWeightToSKU:
        return selectedConditions?.some((conditionGroup: AutomationRulesActions.SelectedCondition[]) => {
          return conditionGroup.some((selectedCondition: AutomationRulesActions.SelectedCondition) => {
            switch (selectedCondition.name) {
              case 'orderStatusFix':
                return selectedCondition.value?.statusArray?.some(
                  (status) => status?.value == OrderStatus.FIX_MEASURES,
                );
            }
            return false;
          });
        });
      case 'changeOrderStatus':
      case 'changeOrderStatusWithDelay':
        if (selectedAction.value == OrderStatus.STEP_ASSIGN || selectedAction.value.value == OrderStatus.STEP_ASSIGN) {
          return selectedConditions?.every((conditionGroup: AutomationRulesActions.SelectedCondition[]) => {
            return conditionGroup.every((selectedCondition: AutomationRulesActions.SelectedCondition) => {
              switch (selectedCondition.name) {
                case 'orderStatusFix':
                  return selectedCondition.value?.statusArray?.every((status) => !status?.value.includes('fix'));
              }
              return true;
            });
          });
        }
        break;
    }
    return true;
  }

  onClose(): void {
    this.dialogRef.close();
  }

  checkSplitByActionValidity(actionValue: {
    value: string;
    operator?: string;
    operator2?: string;
    productTags?: RulesTag[];
    freeTextValue?: string;
    tagName?: string;
  }) {
    if (actionValue?.value === 'sku' || actionValue?.value === 'line_item' || actionValue?.value === 'stock_status') {
      return true;
    }

    if (
      actionValue?.value === 'product_tag' &&
      actionValue.productTags?.length &&
      actionValue.operator &&
      ['includes', 'excludes'].includes(actionValue?.operator2)
    ) {
      return true;
    }

    if (
      actionValue?.value === 'product_tag' &&
      actionValue.freeTextValue?.length &&
      actionValue.operator &&
      ['contains', 'notContains'].includes(actionValue.operator2)
    ) {
      return true;
    }

    if (
      actionValue?.value === 'sku_list' &&
      actionValue.operator &&
      ((actionValue.productTags?.length && ['includes', 'excludes'].includes(actionValue?.operator2)) ||
        (actionValue.freeTextValue?.length && ['contains', 'notContains'].includes(actionValue.operator2)))
    ) {
      return true;
    }

    return false;
  }
}
